/** Keep body-mounted menus anchored to their control and scroll only their option pane. */
export function installBodyDropdownPlacement(ts: any, preferUp: boolean): void {
    const { dropdown, dropdown_content: content, control } = ts;
    const initialMaxHeight = content.style.getPropertyValue('max-height');
    const initialMaxPriority = content.style.getPropertyPriority('max-height');
    const place = () => {
        if (!ts.isOpen) return;
        const controlRect = control.getBoundingClientRect();
        const oldHeight = content.clientHeight;
        // Read the authored limit afresh: vh limits change on resize, and a previously clamped
        // menu must be allowed to grow again. Preserve the normal height when it already fits.
        if (initialMaxHeight) content.style.setProperty('max-height', initialMaxHeight, initialMaxPriority);
        else content.style.removeProperty('max-height');
        const css = getComputedStyle(content);
        const authoredMax = parseFloat(css.maxHeight) || Infinity;
        const margin = 8, gap = 4;
        const above = Math.max(0, controlRect.top - gap - margin);
        const below = Math.max(0, window.innerHeight - controlRect.bottom - gap - margin);
        const naturalHeight = dropdown.getBoundingClientRect().height;
        const up = preferUp || (below < Math.min(160, naturalHeight) && above > below);
        const available = up ? above : below;
        const chrome = dropdown.offsetHeight - content.offsetHeight;
        const contentBox = css.boxSizing === 'border-box' ? 0
            : parseFloat(css.paddingTop) + parseFloat(css.paddingBottom)
                + parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
        content.style.setProperty('max-height', `${Math.max(0, Math.min(authoredMax, available - chrome - contentBox))}px`, 'important');
        const height = dropdown.getBoundingClientRect().height;
        const top = up ? controlRect.top - height - gap : controlRect.bottom + gap;
        dropdown.style.setProperty('margin-top', '0');
        dropdown.style.setProperty('position', 'absolute');
        dropdown.style.setProperty('top', `${window.scrollY + Math.max(margin, Math.min(top, window.innerHeight - margin - height))}px`);
        dropdown.style.setProperty('bottom', 'auto');
        if (preferUp) {
            // Bottom-dock icon pickers use the wrapper's authored width, not its hidden input.
            const anchor = ts.wrapper.getBoundingClientRect();
            dropdown.style.setProperty('left', `${Math.round(anchor.left + window.scrollX)}px`);
            for (const key of ['width', 'min-width', 'max-width'])
                dropdown.style.setProperty(key, `${Math.round(anchor.width)}px`, 'important');
            dropdown.style.setProperty('box-sizing', 'border-box', 'important');
            dropdown.style.setProperty('z-index', '30020');
        }
        if (content.clientHeight < oldHeight && ts.activeOption) {
            const active = ts.activeOption.getBoundingClientRect();
            const pane = content.getBoundingClientRect();
            if (active.bottom > pane.bottom) content.scrollTop += active.bottom - pane.bottom;
            else if (active.top < pane.top) content.scrollTop += active.top - pane.top;
        }
    };
    const defaultPosition = ts.positionDropdown;
    ts.positionDropdown = function (this: any, ...args: any[]) {
        defaultPosition.apply(this, args);
        place();
    };
    // Capture nested card/page scrolling too. Scrolling the option pane keeps its anchor still.
    const reposition = (event: Event) => {
        if (!ts.isOpen) return;
        if (event.target instanceof Node && dropdown.contains(event.target)) return;
        ts.positionDropdown();
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(dropdown);
    observer?.observe(control);
    const defaultDestroy = ts.destroy;
    ts.destroy = function (this: any, ...args: any[]) {
        observer?.disconnect();
        window.removeEventListener('resize', reposition);
        window.removeEventListener('scroll', reposition, true);
        return defaultDestroy.apply(this, args);
    };
}
